import React, { useCallback, useMemo } from 'react';

import { Scroller } from '@interlayer/js/app/components/common/Scroller';
import { IframeTile } from '../GameTile/components/IframeTile';
import { IFrameWidget } from './components/IFrameWidget';
import { PageScroll } from './components/PageScroll';

import { IFRAME_WIDGET } from 'js/app/components/main/Games/constants';
import { useCustomCategory } from 'js/app/components/main/Games/hooks/useCustomCategory';
import { useGameComponentConfig } from 'js/app/components/main/Games/hooks/useGameComponentConfig';
import { useIsAllCategories } from 'js/app/components/main/Games/hooks/useIsAllCategories';
import { useGamesSelector } from 'js/app/components/main/Games/store/context';
import { useInDesktop } from 'js/app/hooks/device/hooks/useMediaQuery';
import { clsx } from 'js/app/utils/clsx.util';

import { GameTile } from '../GameTile';

import style from '../../Games.scss';

export const GameLayout = ({
    gameList = [],
    gameCodes,
    viewModeInline: viewModeInlineProp,
    inlineGrid,
    category = {},
    classNames = {},
    isShowJackpots,
    isShowFavourites,
    isShowInfo,
    isBingo,
    withBands,
    isShowSashes,
    feedName
}) => {
    const isDesktop = useInDesktop();
    const { viewModeDetailed, hub2ViewMode } = useGamesSelector();
    const customCategory = useCustomCategory(category.name);
    const { viewMode } = useGameComponentConfig();
    const isAllCategories = useIsAllCategories();
    const viewModeInline = viewModeInlineProp || viewMode === 'inline' || hub2ViewMode === 'inline' || isAllCategories;

    const { categoryName } = customCategory;

    const getGameCodes = useCallback(() => {
        if (Array.isArray(gameCodes)) {
            return gameCodes;
        }

        return gameList.map(gameObj => {
            if (gameObj.seoFriendlyGameName) {
                return gameObj.seoFriendlyGameName;
            }

            if (isDesktop && gameObj.mobileGameCode) {
                return gameObj.mobileGameCode;
            }

            return gameObj.gameCode;
        });
    }, [gameCodes, gameList, isDesktop]);

    const gameChildren = useMemo(() => {
        return gameList.map((game, index) => (
            <React.Fragment key={`${game.gameCode}${index}`}>
                {game.gameType === IFRAME_WIDGET
                    ? <IframeTile widgetData={game} />
                    : (
                        <GameTile
                            index={index}
                            categoryName={categoryName || category.name}
                            gameData={game}
                            isBingo={isBingo}
                            gameCodes={getGameCodes()}
                            viewModeInline={viewModeDetailed && viewModeInline}
                            classNames={classNames}
                            isShowJackpots={isShowJackpots}
                            isShowFavourites={isShowFavourites}
                            isShowInfo={isShowInfo}
                            withBands={withBands}
                            inlineGrid={isAllCategories ? inlineGrid : false}
                            isShowSashes={isShowSashes}
                            feedName={feedName}
                        />
                    )
                }
            </React.Fragment>
        ));
    }, [
        category.name, categoryName, classNames, gameList, getGameCodes, isAllCategories, inlineGrid, isBingo,
        isShowFavourites, isShowInfo, isShowJackpots, isShowSashes, viewModeDetailed, viewModeInline, withBands,
        feedName
    ]);

    const renderListHeader = () => {
        if (viewModeDetailed) {
            return;
        }

        return (
            <div className={clsx(style.listHeader, { [style.marginTop]: !isAllCategories })}>
                <div className={style.headerCount}>{`${gameList.length} game${gameList.length === 1 ? '' : 's'}`}</div>

                <div className={style.headerName}>Name</div>

                <div className={style.headerJackpot} />
            </div>
        );
    };

    const IFrameWidgetWithProps = useCallback(() => (
        <IFrameWidget
            inAllCategory={isAllCategories}
            viewModeDetailed={viewModeDetailed}
            customCategory={customCategory}
        />
    ), [customCategory, isAllCategories, viewModeDetailed]);

    return (
        viewModeDetailed && viewModeInline
            ? (
                <>
                    {renderListHeader()}

                    <IFrameWidgetWithProps />

                    <Scroller
                        gridSize={inlineGrid}
                        classNames={classNames}
                    >
                        {gameChildren}
                    </Scroller>
                </>
            )
            : (
                <>
                    {!viewModeDetailed && renderListHeader()}

                    <IFrameWidgetWithProps />

                    <PageScroll
                        isBingo={isBingo}
                        classNames={classNames}
                    >
                        {gameChildren}
                    </PageScroll>
                </>
            )
    );

};
